import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MyModal, MyButton } from '../../components';
import { useAuth, useNotification, useSectors } from '../../hooks';
import { hasuraClient } from '../../utils/graphqlClient';
import { GET_FIRMS_COMMUNICATION_REPORT } from '../../graphql/queries';
import { exportFirmCommunicationReport } from '../../utils/exportFirmCommunicationReport';

const ACTIVE_JOB_FILTERS = ['all', 'with_active', 'without_active'];

const FirmCommunicationReportModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const [step, setStep] = useState('form');
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [activeJobsFilter, setActiveJobsFilter] = useState('all');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFirms, setPreviewFirms] = useState([]);
  const [downloading, setDownloading] = useState(false);

  // Sektör listesi standart hook üzerinden gelir; bu raporu kullanma yetkisi
  // verilen kullanıcı sectors tablosuna da read permission'a sahip olmalı.
  const { sectors, isLoading: sectorsLoading } = useSectors();
  const sectorOptions = useMemo(
    () =>
      (sectors || [])
        .map(s => ({ id: s.id, label: s.sector_name || s.name || `#${s.id}` }))
        .sort((a, b) => a.label.localeCompare(b.label, 'tr')),
    [sectors]
  );

  const resetState = useCallback(() => {
    setStep('form');
    setSelectedSectors([]);
    setActiveJobsFilter('all');
    setPreviewFirms([]);
  }, []);

  // Modal kapanınca state'i sıfırla.
  useEffect(() => {
    if (!open) resetState();
  }, [open, resetState]);

  const handleClose = () => {
    if (downloading || previewLoading) return;
    onClose();
  };

  const handlePreview = async () => {
    if (selectedSectors.length === 0 || !user?.companyId) return;
    setPreviewLoading(true);
    try {
      const data = await hasuraClient.query(GET_FIRMS_COMMUNICATION_REPORT, {
        companyId: user.companyId,
        sectorIds: selectedSectors.map(s => s.id),
      });
      // Aktif iş filtresi client-side: query her firma için sadece aktif
      // job_status'lu işleri 1 limitle getiriyor → firms[].jobs.length>0
      // demek "aktif iş var" demek.
      const allFirms = data?.firms || [];
      const filtered = allFirms.filter(f => {
        const hasActive = (f.jobs || []).length > 0;
        if (activeJobsFilter === 'with_active') return hasActive;
        if (activeJobsFilter === 'without_active') return !hasActive;
        return true;
      });
      setPreviewFirms(filtered);
      setStep('preview');
    } catch (err) {
      const msg = err?.message || t('firms.communicationReport.errorToast');
      showNotification(msg, 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async () => {
    if (previewFirms.length === 0) return;
    setDownloading(true);
    try {
      exportFirmCommunicationReport(previewFirms);
      showNotification(
        t('firms.communicationReport.successToast'),
        'success'
      );
    } catch (err) {
      const msg = err?.message || t('firms.communicationReport.errorToast');
      showNotification(msg, 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleBackToForm = () => {
    setStep('form');
  };

  const totalContacts = useMemo(
    () =>
      previewFirms.reduce(
        (sum, f) => sum + (Array.isArray(f?.firm_carts) ? f.firm_carts.length : 0),
        0
      ),
    [previewFirms]
  );

  const getSectorsLabel = firm => {
    const list = Array.isArray(firm?.firm_sectors) ? firm.firm_sectors : [];
    return list
      .map(fs => fs?.sector?.sector_name)
      .filter(Boolean)
      .join(', ');
  };

  const formActions = (
    <>
      <MyButton
        variant="outlined"
        onClick={handleClose}
        disabled={previewLoading}
      >
        {t('firms.communicationReport.cancel')}
      </MyButton>
      <MyButton
        variant="contained"
        onClick={handlePreview}
        disabled={
          previewLoading || sectorsLoading || selectedSectors.length === 0
        }
        startIcon={
          previewLoading ? <CircularProgress size={16} color="inherit" /> : null
        }
      >
        {previewLoading
          ? t('firms.communicationReport.loading')
          : t('firms.communicationReport.preview')}
      </MyButton>
    </>
  );

  const previewActions = (
    <>
      <MyButton
        variant="outlined"
        onClick={handleBackToForm}
        disabled={downloading}
      >
        {t('firms.communicationReport.regenerate')}
      </MyButton>
      <MyButton
        variant="contained"
        onClick={handleDownload}
        disabled={downloading || previewFirms.length === 0}
        startIcon={
          downloading ? <CircularProgress size={16} color="inherit" /> : null
        }
      >
        {downloading
          ? t('firms.communicationReport.loading')
          : t('firms.communicationReport.download')}
      </MyButton>
    </>
  );

  const renderFormStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          {t('firms.communicationReport.selectSectors')}
        </Typography>
        <MyButton
          size="small"
          variant="text"
          onClick={() => {
            // Tek tıkla tüm sektörleri seç (zaten hepsi seçiliyse temizle).
            if (selectedSectors.length === sectorOptions.length) {
              setSelectedSectors([]);
            } else {
              setSelectedSectors(sectorOptions);
            }
          }}
          disabled={sectorsLoading || previewLoading || sectorOptions.length === 0}
        >
          {selectedSectors.length === sectorOptions.length && sectorOptions.length > 0
            ? t('firms.communicationReport.clearSectors', 'Seçimi Temizle')
            : t('firms.communicationReport.allSectors', 'Tüm Sektörler')}
        </MyButton>
      </Box>
      <Autocomplete
        multiple
        options={sectorOptions}
        value={selectedSectors}
        loading={sectorsLoading}
        onChange={(_, value) => setSelectedSectors(value)}
        getOptionLabel={option => option.label || ''}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        noOptionsText={t('firms.communicationReport.noSectors')}
        disabled={previewLoading}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              size="small"
              label={option.label}
              {...getTagProps({ index })}
              key={option.id}
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={t('firms.communicationReport.selectSectors')}
          />
        )}
      />

      <FormControl component="fieldset" disabled={previewLoading}>
        <FormLabel component="legend" sx={{ mb: 1 }}>
          {t('firms.communicationReport.activeJobsFilter.label')}
        </FormLabel>
        <RadioGroup
          row
          value={activeJobsFilter}
          onChange={e => setActiveJobsFilter(e.target.value)}
        >
          {ACTIVE_JOB_FILTERS.map(value => (
            <FormControlLabel
              key={value}
              value={value}
              control={<Radio size="small" />}
              label={t(
                `firms.communicationReport.activeJobsFilter.${
                  value === 'with_active'
                    ? 'withActive'
                    : value === 'without_active'
                      ? 'withoutActive'
                      : 'all'
                }`
              )}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Box>
  );

  const renderPreviewStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t('firms.communicationReport.previewView.title')}
        </Typography>
        <Chip
          size="small"
          label={t('firms.communicationReport.previewView.firmsCount', {
            count: previewFirms.length,
          })}
        />
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={t('firms.communicationReport.previewView.contactsCount', {
            count: totalContacts,
          })}
        />
      </Stack>

      {previewFirms.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {t('firms.communicationReport.previewView.empty')}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  {t('firms.communicationReport.previewView.tableHeaders.firmName')}
                </TableCell>
                <TableCell>
                  {t('firms.communicationReport.previewView.tableHeaders.sectors')}
                </TableCell>
                <TableCell align="right">
                  {t('firms.communicationReport.previewView.tableHeaders.contactCount')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previewFirms.map(firm => {
                const carts = Array.isArray(firm?.firm_carts) ? firm.firm_carts : [];
                return (
                  <React.Fragment key={firm.id}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {firm.firm_name || firm.firm_short_name || ''}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                        {getSectorsLabel(firm)}
                      </TableCell>
                      <TableCell align="right">{carts.length}</TableCell>
                    </TableRow>
                    {carts.map((cart, idx) => {
                      const fullName = [cart?.firm_cart_firstname, cart?.firm_cart_lastname]
                        .filter(Boolean)
                        .join(' ')
                        .trim();
                      const contactBits = [
                        fullName,
                        cart?.firm_cart_title,
                        cart?.firm_cart_mail,
                        cart?.firm_cart_phone,
                      ]
                        .filter(Boolean)
                        .join(' • ');
                      return (
                        <TableRow
                          key={`${firm.id}-cart-${idx}`}
                          sx={{ '& td': { borderBottom: 'none', py: 0.25 } }}
                        >
                          <TableCell sx={{ pl: 4, color: 'text.secondary', fontSize: 12 }} colSpan={3}>
                            {contactBits || '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  return (
    <MyModal
      open={open}
      onClose={handleClose}
      title={t('firms.communicationReport.modalTitle')}
      maxWidth={step === 'preview' ? 'md' : 'sm'}
      actions={step === 'preview' ? previewActions : formActions}
      disableBackdropClose={downloading || previewLoading}
      disableEscapeKeyDown={downloading || previewLoading}
    >
      {step === 'preview' ? renderPreviewStep() : renderFormStep()}
    </MyModal>
  );
};

export default FirmCommunicationReportModal;
